import React, { useState, useEffect } from 'react';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Link2, 
  Plus, 
  Trash2, 
  Play, 
  CheckCircle, 
  XCircle,
  Calendar,
  Clock,
  Settings2,
  ExternalLink,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

export default function VoiceIntegrations() {
  const { fetchApi } = useCloudAuth();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [schedulingConfig, setSchedulingConfig] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
    
    // Check for Google OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      setSuccess('Google Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_error')) {
      setError(`Google Calendar connection failed: ${params.get('google_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [integrationsRes, configRes, googleRes] = await Promise.all([
        fetchApi('/api/integrations/api-integrations'),
        fetchApi('/api/integrations/scheduling/config'),
        fetchApi('/api/integrations/calendar/google/status')
      ]);
      
      setIntegrations(integrationsRes.data || []);
      setSchedulingConfig(configRes.data);
      setGoogleStatus(googleRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTestIntegration = async (id, variableName) => {
    setTestingId(id);
    try {
      const res = await fetchApi('/api/integrations/api-integrations/call', {
        method: 'POST',
        body: JSON.stringify({ variableName, context: {} })
      });
      
      if (res.success) {
        setSuccess(`API call successful! Response: ${JSON.stringify(res.data).substring(0, 100)}...`);
      } else {
        setError(`API call failed: ${res.error}`);
      }
      await loadData();
    } catch (err) {
      setError('Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteIntegration = async (id) => {
    if (!window.confirm('Delete this integration?')) return;
    
    try {
      await fetchApi(`/api/integrations/api-integrations/${id}`, { method: 'DELETE' });
      setSuccess('Integration deleted');
      await loadData();
    } catch (err) {
      setError('Delete failed');
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await fetchApi('/api/integrations/calendar/google/connect');
      if (res.data?.authUrl) {
        window.location.href = res.data.authUrl;
      }
    } catch (err) {
      setError('Failed to initiate Google connection');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    
    try {
      await fetchApi('/api/integrations/calendar/google/disconnect', { method: 'POST' });
      setSuccess('Google Calendar disconnected');
      await loadData();
    } catch (err) {
      setError('Disconnect failed');
    }
  };

  const handleSaveScheduling = async (updates) => {
    try {
      await fetchApi('/api/integrations/scheduling/config', {
        method: 'POST',
        body: JSON.stringify(updates)
      });
      setSuccess('Scheduling settings saved');
      await loadData();
    } catch (err) {
      setError('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading integrations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voice-integrations">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Integrations</h2>
          <p className="text-sm text-gray-400 mt-1">
            Connect external APIs and configure scheduling
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-integration-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add API Integration
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-300">×</button>
        </div>
      )}

      {/* Google Calendar Connection */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Google Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              {googleStatus?.connected ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400">Connected</span>
                  <span className="text-gray-400">({googleStatus.email})</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-400">Not connected</span>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Connect to sync availability and create calendar events for bookings
              </p>
            </div>
            {googleStatus?.connected ? (
              <Button
                variant="outline"
                onClick={handleDisconnectGoogle}
                className="text-red-400 border-red-400/30 hover:bg-red-400/10"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={handleConnectGoogle}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="connect-google-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect Google Calendar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Scheduling Configuration */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            Scheduling Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {schedulingConfig && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Timezone
                  </label>
                  <select
                    value={schedulingConfig.timezone}
                    onChange={(e) => handleSaveScheduling({ timezone: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    data-testid="timezone-select"
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="UTC">UTC</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Slot Duration (min)
                  </label>
                  <select
                    value={schedulingConfig.slotDurationMins}
                    onChange={(e) => handleSaveScheduling({ slotDurationMins: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Min Notice (hours)
                  </label>
                  <select
                    value={schedulingConfig.minNoticeHours}
                    onChange={(e) => handleSaveScheduling({ minNoticeHours: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="1">1 hour</option>
                    <option value="2">2 hours</option>
                    <option value="4">4 hours</option>
                    <option value="24">24 hours</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Working Hours
                </label>
                <div className="space-y-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                    const wh = schedulingConfig.workingHours?.find(w => w.day === idx) || {
                      day: idx,
                      enabled: idx < 5,
                      start: '09:00',
                      end: '17:00'
                    };
                    return (
                      <div key={day} className="flex items-center gap-4 p-2 bg-gray-800/50 rounded-lg">
                        <label className="flex items-center gap-2 w-24">
                          <input
                            type="checkbox"
                            checked={wh.enabled}
                            onChange={(e) => {
                              const updated = schedulingConfig.workingHours.map(w =>
                                w.day === idx ? { ...w, enabled: e.target.checked } : w
                              );
                              handleSaveScheduling({ workingHours: updated });
                            }}
                            className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-purple-500"
                          />
                          <span className="text-gray-300">{day}</span>
                        </label>
                        <input
                          type="time"
                          value={wh.start}
                          onChange={(e) => {
                            const updated = schedulingConfig.workingHours.map(w =>
                              w.day === idx ? { ...w, start: e.target.value } : w
                            );
                            handleSaveScheduling({ workingHours: updated });
                          }}
                          disabled={!wh.enabled}
                          className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white disabled:opacity-50"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                          type="time"
                          value={wh.end}
                          onChange={(e) => {
                            const updated = schedulingConfig.workingHours.map(w =>
                              w.day === idx ? { ...w, end: e.target.value } : w
                            );
                            handleSaveScheduling({ workingHours: updated });
                          }}
                          disabled={!wh.enabled}
                          className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white disabled:opacity-50"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Buffer Before (min)
                  </label>
                  <input
                    type="number"
                    value={schedulingConfig.bufferBeforeMins}
                    onChange={(e) => handleSaveScheduling({ bufferBeforeMins: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                    max="60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Buffer After (min)
                  </label>
                  <input
                    type="number"
                    value={schedulingConfig.bufferAfterMins}
                    onChange={(e) => handleSaveScheduling({ bufferAfterMins: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    min="0"
                    max="60"
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* API Integrations */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-purple-400" />
            Custom API Integrations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400 mb-4">
            Connect your own APIs. Use variable references in prompts like{' '}
            <code className="bg-gray-800 px-2 py-1 rounded text-purple-400">{'{order_status}'}</code>
          </p>
          
          {integrations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No API integrations configured yet.
              <br />
              <Button
                variant="link"
                onClick={() => setShowAddModal(true)}
                className="text-purple-400 mt-2"
              >
                Add your first integration
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {integrations.map((integration) => (
                <div
                  key={integration.id}
                  className="p-4 bg-gray-800/50 rounded-lg border border-gray-700"
                  data-testid={`integration-${integration.variableName}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-white">{integration.name}</span>
                        <code className="bg-gray-700 px-2 py-0.5 rounded text-sm text-purple-400">
                          {`{${integration.variableName}}`}
                        </code>
                        <span className="text-xs text-gray-500">{integration.method}</span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1 truncate max-w-lg">
                        {integration.endpointUrl}
                      </p>
                      {integration.lastTestStatus && (
                        <div className="flex items-center gap-2 mt-1">
                          {integration.lastTestStatus === 'success' ? (
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-400" />
                          )}
                          <span className="text-xs text-gray-500">
                            Last test: {integration.lastTestStatus}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestIntegration(integration.id, integration.variableName)}
                        disabled={testingId === integration.id}
                        className="text-blue-400 border-blue-400/30 hover:bg-blue-400/10"
                      >
                        {testingId === integration.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                        <span className="ml-1">Test</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteIntegration(integration.id)}
                        className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Integration Modal */}
      {showAddModal && (
        <AddIntegrationModal
          onClose={() => setShowAddModal(false)}
          onSave={async (data) => {
            try {
              await fetchApi('/api/integrations/api-integrations', {
                method: 'POST',
                body: JSON.stringify(data)
              });
              setSuccess('Integration created successfully');
              setShowAddModal(false);
              await loadData();
            } catch (err) {
              setError(err.message || 'Failed to create integration');
            }
          }}
        />
      )}
    </div>
  );
}


function AddIntegrationModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    name: '',
    variableName: '',
    endpointUrl: '',
    method: 'GET',
    authType: 'none',
    authConfig: {},
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      name: form.name,
      variableName: form.variableName.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
      endpointUrl: form.endpointUrl,
      method: form.method,
      authType: form.authType,
      description: form.description
    };
    
    if (form.authType === 'bearer' && form.authConfig.token) {
      data.authConfig = { token: form.authConfig.token };
    } else if (form.authType === 'api_key' && form.authConfig.api_key) {
      data.authConfig = { 
        api_key: form.authConfig.api_key,
        header_name: form.authConfig.header_name || 'X-API-Key'
      };
    }
    
    onSave(data);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Add API Integration</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Order Status API"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Variable Name *
            </label>
            <input
              type="text"
              value={form.variableName}
              onChange={(e) => setForm({ ...form, variableName: e.target.value })}
              placeholder="order_status"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Use this in prompts as {`{${form.variableName || 'variable_name'}}`}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Endpoint URL *
            </label>
            <input
              type="url"
              value={form.endpointUrl}
              onChange={(e) => setForm({ ...form, endpointUrl: e.target.value })}
              placeholder="https://api.example.com/orders/{order_id}"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Method
              </label>
              <select
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Authentication
              </label>
              <select
                value={form.authType}
                onChange={(e) => setForm({ ...form, authType: e.target.value, authConfig: {} })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              >
                <option value="none">None</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key</option>
              </select>
            </div>
          </div>
          
          {form.authType === 'bearer' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Bearer Token
              </label>
              <input
                type="password"
                value={form.authConfig.token || ''}
                onChange={(e) => setForm({ 
                  ...form, 
                  authConfig: { ...form.authConfig, token: e.target.value }
                })}
                placeholder="Enter bearer token"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
            </div>
          )}
          
          {form.authType === 'api_key' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  value={form.authConfig.api_key || ''}
                  onChange={(e) => setForm({ 
                    ...form, 
                    authConfig: { ...form.authConfig, api_key: e.target.value }
                  })}
                  placeholder="Enter API key"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Header Name
                </label>
                <input
                  type="text"
                  value={form.authConfig.header_name || 'X-API-Key'}
                  onChange={(e) => setForm({ 
                    ...form, 
                    authConfig: { ...form.authConfig, header_name: e.target.value }
                  })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does this API do?"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none h-20"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-purple-600 hover:bg-purple-700"
            >
              Create Integration
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
